import { CollapsibleContent } from "@radix-ui/react-collapsible";
import { motion } from "framer-motion";
import { observer } from "mobx-react-lite";
import { useCallback, useEffect, useState } from "react";

import { useContentAreaSize } from "../../../../size-context";
import { getSnippetSizeClasses } from "../../utils";

import SnippetContent from "./SnippetContent";
import SnippetHeader from "./SnippetHeader";
import useSnippetOverrideReaction from "./hooks/useSnippetOverrideReaction";

import { TemplateFormat } from "@/components/Playground/types";
import { Skeleton } from "@/components/ui/skeleton";

interface SnippetViewProps {
  promptName: string;
  version?: number;
  label?: string;
  prompt?: string;
  onEdit?: () => void;
  isCollapsed?: boolean;
  toggleCollapsed?: () => void;
  isLoading?: boolean;
  href?: string;
  isReadOnly?: boolean;
  sourcedPrompt?: string;
  format?: TemplateFormat;
  promptId?: number;
  onVersionLabelChange?: (version: number | undefined, label: string | undefined) => void;
}

const normalizeSnippetContent = (value?: string): string =>
  (value ?? "")
    .replace(/\r\n/g, "\n")
    .replace(/[ \t]+\n/g, "\n")
    .trimEnd();

const SnippetView = observer(
  ({
    promptName,
    version,
    label,
    prompt: initialPrompt,
    onEdit,
    isCollapsed,
    toggleCollapsed,
    isLoading = false,
    sourcedPrompt,
    href,
    isReadOnly = false,
    format,
    promptId,
    onVersionLabelChange,
  }: SnippetViewProps) => {
    const resolvedPrompt = sourcedPrompt ?? initialPrompt ?? undefined;
    const [prompt, setPrompt] = useState(resolvedPrompt);

    const [resetKey, setResetKey] = useState(0);
    const size = useContentAreaSize();

    const normalizedInitialPrompt = normalizeSnippetContent(initialPrompt);
    const normalizedResolvedPrompt = normalizeSnippetContent(resolvedPrompt);

    const [hasChanged, setHasChanged] = useState(
      sourcedPrompt !== undefined &&
        normalizeSnippetContent(sourcedPrompt) !== normalizedInitialPrompt,
    );

    useEffect(() => {
      setHasChanged(normalizeSnippetContent(prompt) !== normalizedResolvedPrompt);
    }, [prompt, normalizedResolvedPrompt]);

    useEffect(() => {
      setPrompt(resolvedPrompt);
      setHasChanged(
        sourcedPrompt !== undefined &&
          normalizeSnippetContent(sourcedPrompt) !== normalizedInitialPrompt,
      );
    }, [resolvedPrompt, sourcedPrompt, normalizedInitialPrompt]);

    useSnippetOverrideReaction(
      {
        prompt_name: promptName,
        version_number: version,
        label,
      },
      prompt,
      hasChanged,
    );

    const resetPrompt = useCallback(() => {
      setPrompt(initialPrompt || undefined);
      setResetKey(prev => prev + 1);
      setHasChanged(false);
    }, [initialPrompt]);

    const handleVersionLabelChange = useCallback(
      (newVersion: number | undefined, newLabel: string | undefined) => {
        setHasChanged(false);
        onVersionLabelChange?.(newVersion, newLabel);
      },
      [onVersionLabelChange],
    );

    const hasSnippet = Boolean(promptName);

    return (
      <>
        <SnippetHeader
          name={promptName}
          version={version}
          label={label}
          onEdit={onEdit ? onEdit : undefined}
          isCollapsed={isCollapsed}
          toggleCollapsed={toggleCollapsed}
          hasChanged={hasChanged && !isReadOnly}
          onReset={resetPrompt}
          href={href}
          isReadOnly={isReadOnly}
          promptId={promptId}
          onVersionLabelChange={handleVersionLabelChange}
        />
        {hasSnippet && (
          <CollapsibleContent forceMount asChild>
            <motion.div
              initial={false}
              animate={{
                height: isCollapsed ? 0 : "auto",
                opacity: isCollapsed ? 0 : 1,
              }}
              transition={{
                height: {
                  duration: 0.3,
                  ease: [0.4, 0, 0.2, 1],
                },
                opacity: {
                  duration: 0.2,
                  ease: "easeInOut",
                },
              }}
              style={{ overflow: "hidden" }}
            >
              {isLoading || prompt === undefined ? (
                <motion.div
                  initial={{ opacity: 0, y: -4 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{
                    duration: 0.2,
                    ease: "easeOut",
                  }}
                  className={`flex flex-col gap-y-1 ${getSnippetSizeClasses(size, "padding")}`}
                >
                  <Skeleton height="1rem" />
                  <Skeleton height="1rem" />
                  <Skeleton height="1rem" width="75%" />
                </motion.div>
              ) : (
                <SnippetContent
                  key={resetKey}
                  prompt={prompt}
                  onPromptChange={setPrompt}
                  hasChanged={hasChanged && !isReadOnly}
                  isReadOnly={isReadOnly}
                  promptName={promptName}
                  version={version}
                  label={label}
                  format={format}
                />
              )}
            </motion.div>
          </CollapsibleContent>
        )}
      </>
    );
  },
);

export default SnippetView;
